import { Typography } from '@euler/app/components/typography';
import { InspectionResultFormatter } from '@euler/app/flows/report/functions';
import { DefectiveBadge } from '@euler/app/tv/components/DefectiveBadge';
import { Panel } from '@euler/app/tv/components/Panel';
import { customIssueToReportItem } from '@euler/app/tv/functions/customIssueToReportItem';
import { inspectedItemToReportItem } from '@euler/app/tv/functions/inspectedItemToReportItem';
import { useDefectiveBadgeColorMap } from '@euler/app/tv/functions/useDefectiveBadgeColorMap';
import { useScreenContentPadding } from '@euler/app/tv/functions/utils';
import { usePresentationTheme } from '@euler/app/tv/theme';
import { HStack } from '@euler/components/layout/Stack';
import { getDefectiveLevel } from '@euler/functions/getDefectiveLevel';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { InventoryQuery } from '@euler/functions/useInventory';
import { DefectiveLevel, DefectiveLevelColorMap } from '@euler/model';
import { AbnormalLevel } from '@euler/model/enum';
import { InspectionReportItem } from '@euler/model/report';
import { TaskDetail } from '@euler/model/task-detail';
import { comparerDesc } from '@euler/utils';
import { arr2groupmap } from '@euler/utils/array';
import { memo, useCallback, useMemo } from 'react';
import {
  FlatList,
  ListRenderItem,
  Platform,
  StyleProp,
  Text,
  View,
  ViewStyle,
} from 'react-native';

type WorkItemDetail = {
  name: string;
  label: string;
  valueRange?: string;
  customIssueId?: number | null;
  defectiveLevel: DefectiveLevel;
};

type WorkItem = {
  name: string;
  defectiveLevel: DefectiveLevel;
  details: WorkItemDetail[];
};

export const useUnscheduledJobs = (
  detail: TaskDetail,
  inventoryQuery: InventoryQuery,
) => {
  return useMemo<WorkItem[]>(() => {
    const res: WorkItem[] = [];
    const jobNameSet = new Set(detail.construction.jobs.map(x => x.name));
    const reportItems: InspectionReportItem[] = [];
    for (const inspectedSite of detail.inspection.sites) {
      const site = inventoryQuery.getSiteById(inspectedSite.siteId);
      for (const inspectedItem of inspectedSite.items) {
        if (inspectedItem.abnormalLevel !== AbnormalLevel.Fine) {
          const reportItem = inspectedItemToReportItem({
            site,
            inspectedSite,
            inspectedItem,
          });
          if (reportItem.maintenanceAdvice) {
            reportItems.push(reportItem);
          }
        }
      }
    }
    if (detail.inspection.customIssues != null) {
      for (const customIssue of detail.inspection.customIssues) {
        if (customIssue.abnormalLevel !== AbnormalLevel.Fine) {
          const reportItem = customIssueToReportItem({ customIssue });
          if (reportItem.maintenanceAdvice) {
            reportItems.push(reportItem);
          }
        }
      }
    }
    const groups = arr2groupmap(reportItems, x => x.maintenanceAdvice!);
    for (const [jobName, list] of groups.entries()) {
      if (jobNameSet.has(jobName)) continue;
      const defectiveLevels = list
        .map(x => getDefectiveLevel(x.abnormalLevel, x.severityLevel))
        .sort((x, y) => comparerDesc(x, y));
      res.push({
        name: jobName,
        defectiveLevel: defectiveLevels[0],
        details: list.map(reportItem => {
          const name =
            InspectionResultFormatter.getInspectedItemDisplayName(reportItem);
          const [label, valueRange] =
            InspectionResultFormatter.formatLabel(reportItem);
          const defectiveLevel = getDefectiveLevel(
            reportItem.abnormalLevel,
            reportItem.severityLevel,
          );
          return {
            defectiveLevel,
            name,
            label:
              label ?? reportItem.siteItemName ?? reportItem.siteName ?? '',
            valueRange: valueRange ?? undefined,
          };
        }),
      });
    }
    return res;
  }, [detail, inventoryQuery]);
};

export const UnscheduledJobsView = memo(
  ({
    workItems,
    style,
  }: {
    workItems: WorkItem[];
    style?: StyleProp<ViewStyle>;
  }) => {
    const { rpx } = useResponsiveContext();
    const colorMap = useDefectiveBadgeColorMap();
    const [paddingLeft] = useScreenContentPadding();

    const renderItem: ListRenderItem<WorkItem> = useCallback(
      ({ item }) => (
        <UnscheduledJobCell
          workItem={item}
          colorMap={colorMap}
          css={`
            margin-right: ${rpx(40)}px;
          `}
        />
      ),
      [colorMap, rpx],
    );

    return (
      <View style={style}>
        <FlatList
          horizontal
          showsHorizontalScrollIndicator={Platform.OS === 'web'}
          data={workItems}
          keyExtractor={x => x.name}
          renderItem={renderItem}
          contentContainerStyle={{
            paddingLeft,
          }}
        />
      </View>
    );
  },
);

const UnscheduledJobCell = memo(
  ({
    workItem,
    style,
    colorMap,
  }: {
    workItem: WorkItem;
    style?: StyleProp<ViewStyle>;
    colorMap: DefectiveLevelColorMap;
  }) => {
    const theme = usePresentationTheme();
    const { rpx } = useResponsiveContext();
    return (
      <Panel style={style}>
        <HStack
          css={`
            margin-bottom: ${rpx(19)}px;
          `}
        >
          <Typography.Label
            numberOfLines={1}
            regular
            size={rpx(30)}
            color={theme.task.construction.unscheduledJob.titleTextColor}
            css={`
              flex-grow: 1;
            `}
          >
            {workItem.name}
          </Typography.Label>
          <DefectiveBadge
            colorMap={colorMap}
            defectiveLevel={workItem.defectiveLevel}
            css={`
              left: ${rpx(32)}px;
              margin-left: ${rpx(20)}px;
              flex-shrink: 0;
              flex-grow: 0;
            `}
          />
        </HStack>
        {workItem.details.length > 0 ? (
          <View>
            {workItem.details.map((detail, k) => (
              <WorkItemDetailCell key={k} detail={detail} colorMap={colorMap} />
            ))}
          </View>
        ) : null}
      </Panel>
    );
  },
);

const WorkItemDetailCell = memo(
  ({
    colorMap,
    detail,
    style,
  }: {
    detail: WorkItemDetail;
    style?: StyleProp<ViewStyle>;
    colorMap: DefectiveLevelColorMap;
  }) => {
    const color = colorMap[detail.defectiveLevel];
    const theme = usePresentationTheme();
    const { rpx } = useResponsiveContext();
    return (
      <HStack justifyContent="flex-start" style={style}>
        <View
          css={`
            width: ${rpx(6)}px;
            height: ${rpx(6)}px;
            border-radius: ${rpx(6) / 2}px;
            background-color: ${color};
            margin-right: ${rpx(8)}px;
          `}
        />
        <Typography.Label
          light
          color={theme.task.construction.unscheduledJob.labelTextColor}
          size={rpx(24)}
          lineHeight={rpx(36)}
        >
          <Text>{`${detail.name}: `}</Text>
          <Text
            css={`
              color: ${theme.task.construction.unscheduledJob.valueTextColor};
            `}
          >{`${detail.label}${
            detail.valueRange != null ? ' [检测值]' : ''
          }`}</Text>
        </Typography.Label>
        {detail.valueRange != null && (
          <Typography.Label
            light
            color={theme.task.construction.unscheduledJob.valueRangeTextColor}
            size={rpx(24)}
            lineHeight={rpx(36)}
          >
            {`检测结果评估: ${detail.valueRange}`}
          </Typography.Label>
        )}
      </HStack>
    );
  },
);
